import { ComponentPropsWithoutRef, ElementRef, forwardRef, HTMLAttributes } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@utils'
import { clsx } from 'clsx'
import { CloseIcon } from '@components/icons/light/CloseIcon'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = ({
  children,
  ...props
}: DialogPrimitive.DialogPortalProps & { portalClassName?: string }) => (
  <DialogPrimitive.Portal {...props}>
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center sm:items-center',
        props.portalClassName,
      )}
    >
      {children}
    </div>
  </DialogPrimitive.Portal>
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'bg-[#00000066] fixed inset-0 z-50 transition-opacity duration-150 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:duration-100 data-[state=open]:fade-in',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export type DialogContentProps = ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  isClosable?: boolean
  childrenClassName?: string
  title?: string
  hideTitle?: boolean
  afterOverlay?: React.ReactNode
}

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps & { portalClassName?: string; closeButtonClassName?: string }
>(
  (
    {
      isClosable = true,
      className,
      childrenClassName,
      children,
      closeButtonClassName,
      title,
      hideTitle = false,
      afterOverlay,
      ...props
    },
    ref,
  ) => (
    <DialogPortal portalClassName={props.portalClassName}>
      <DialogOverlay />
      {afterOverlay}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'text-cw-midnight-purple fixed z-50 mt-20 grid w-full overflow-y-hidden rounded-4xl bg-white px-5 py-10 animate-in focus:outline-none data-[state=open]:fade-in-90 data-[state=open]:slide-in-from-bottom-10 xs:mt-0 xs:max-w-container-md xs:p-16 sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        <DialogPrimitive.Title
          className={cn(
            hideTitle || !title
              ? 'sr-only'
              : 'text-lg font-semibold leading-none tracking-tight',
          )}
        >
          {title || 'Dialog'}
        </DialogPrimitive.Title>
        <div
          className={cn(
            'max-h-[75dvh] text-cw-midnight-purple overflow-y-auto w-full',
            childrenClassName,
          )}
        >
          {children}
        </div>
        {isClosable && (
          <DialogPrimitive.Close
            type="button"
            onClick={(event) => {
              event.stopPropagation()
            }}
            className={clsx(
              'absolute right-6 top-6 rounded-xl bg-cw-white p-2 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-cw-black-grey',
              closeButtonClassName,
            )}
          >
            <CloseIcon className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('text-left text-h3 xs:text-h2', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
